import type { IntlShape } from 'react-intl'
import { createIntl } from 'react-intl'

import { DEFAULT_LOCALE } from '@app/constants/Locales'

import { INTL_FORMATS } from '@app/utils/IntlUtils'

type IntlMessagesMap = { [key: string]: string }

const buildIntl = (locale: string, messages: IntlMessagesMap | undefined): IntlShape =>
  createIntl({
    defaultFormats: INTL_FORMATS,
    defaultLocale: DEFAULT_LOCALE,
    formats: INTL_FORMATS,
    locale,
    messages,
    onError: (err: Error) => console.error(err.message),
    onWarn: (msg: string) => console.warn(msg),
  })

export class IntlService {
  private fetchTranslations: (locale: string) => Promise<IntlMessagesMap>

  constructor(fetchTranslations: (locale: string) => Promise<IntlMessagesMap>) {
    this.fetchTranslations = fetchTranslations
  }

  async createIntl(locale: string = DEFAULT_LOCALE): Promise<IntlShape> {
    if (locale === DEFAULT_LOCALE) {
      return this.createDefaultIntl()
    }

    const messages = await this.fetchTranslations(locale)
    return buildIntl(locale, messages)
  }

  createDefaultIntl(): IntlShape {
    return buildIntl(DEFAULT_LOCALE, undefined)
  }

  mergeIntl(prev: IntlShape, extra: IntlMessagesMap): IntlShape {
    if (prev.locale === DEFAULT_LOCALE) return prev
    const prevMessages = (prev.messages as IntlMessagesMap | undefined) ?? {}
    let hasNewKeys = false
    for (const key in extra) {
      if (prevMessages[key] !== extra[key]) {
        hasNewKeys = true
        break
      }
    }
    if (!hasNewKeys) return prev
    return buildIntl(prev.locale, { ...prevMessages, ...extra })
  }
}
